import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { TOTAL_MAX } from "../../../../utils/readiness-report";
import { ABOVE_COLOR, BELOW_COLOR } from "./readinessChartTheme";
import {
  formatUtcDay,
  sectionTranslator,
  useIntensity,
} from "./readinessFormat";

const tr = sectionTranslator("chart");

/**
 * Gets the color for an answer.
 * @param {*} answer
 * @returns
 */
const answerColor = (answer) => {
  if (answer.flag === "lower") return BELOW_COLOR;
  return answer.flag === "higher" ? ABOVE_COLOR : undefined;
};

// What the hovered day had: the score and its five answers, and the
// Session RPE, HRV and Resting HR it carried.
export const ReadinessChartTooltip = ({ active, payload, showHrvSource }) => {
  const { t } = useTranslation("athlete-portal");
  const { context, toMeasure } = useIntensity();
  const row = active ? payload?.[0]?.payload : null;
  if (!row) return null;
  const hasOverlay =
    row.sessionRpe != null || row.hrv != null || row.restingHr != null;
  const hrvSourceName = row.hrvSource
    ? tr(t, `tooltip.source.${row.hrvSource}`)
    : "";
  if (row.total == null && !hasOverlay) return null;

  return (
    <div className="rr-tooltip">
      <div className="rr-tooltip__date">{formatUtcDay(row.utcDate)}</div>
      {row.total == null ? null : (
        <>
          <div className="rr-tooltip__row rr-tooltip__row--strong">
            <span>{tr(t, "tooltip.score")}</span>
            <span>
              {row.total}/{TOTAL_MAX} ·{" "}
              {t(`readinessRate.report.status.${row.status}`)}
            </span>
          </div>
          {row.answers.map((answer) => (
            <div key={answer.key} className="rr-tooltip__row">
              <span>{t(`readinessRate.${answer.key}`)}</span>
              <span style={{ color: answerColor(answer) }}>{answer.value}</span>
            </div>
          ))}
        </>
      )}
      {row.sessionRpe == null ? null : (
        <div className="rr-tooltip__row">
          <span>{tr(t, "tooltip.sessionRpe", { context })}</span>
          <span>{toMeasure(row.sessionRpe)}</span>
        </div>
      )}
      {row.hrv == null ? null : (
        <div className="rr-tooltip__row">
          <span>{tr(t, "tooltip.hrv")}</span>
          <span>
            {row.hrv} {tr(t, "units.hrv")}
            {showHrvSource && row.hrvSource ? ` (${hrvSourceName})` : ""}
          </span>
        </div>
      )}
      {row.restingHr == null ? null : (
        <div className="rr-tooltip__row">
          <span>{tr(t, "tooltip.restingHr")}</span>
          <span>
            {row.restingHr} {tr(t, "units.restingHr")}
          </span>
        </div>
      )}
    </div>
  );
};

ReadinessChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  showHrvSource: PropTypes.bool,
};
